import { cloneDeep, debounce, get, set } from "lodash-es";
import React, { FC, useCallback, useState } from "react";
import { useSelector } from "react-redux";

import CloseIcon from "@mui/icons-material/Close";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormLabel,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import Button from "@mui/material/Button";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";

import { CircleMinus, CirclePlus } from "../../../../components/icons";
import TextField from "../../../../components/styled/TextField";
import {
  getRouteFormProperty,
  setRouteFormProperty,
} from "../../../../slices/routes";
import { useDispatch } from "../../../../store";
import { RouteRewriteHeader } from "../../../../store/pomeriumApi";

type RewriteHeadersProps = {
  readonly: boolean;
};

const RewriteHeaders: FC<RewriteHeadersProps> = ({ readonly }) => {
  const dispatch = useDispatch();
  const formValue = useSelector(
    getRouteFormProperty("rewriteResponseHeaders", []),
  ) as RouteRewriteHeader[];
  const [rewriteHeaders, setRewriteHeaders] =
    useState<RouteRewriteHeader[]>(formValue);
  const [open, setOpen] = useState<boolean>(false);
  const [newHeader, setNewHeader] = useState<string>("");
  const [newPrefix, setNewPrefix] = useState<string>("");
  const [newValue, setNewValue] = useState<string>("");

  const onClose = () => {
    setNewHeader("");
    setNewPrefix("");
    setNewValue("");
    setOpen(false);
  };

  const addNewRow = () => {
    setRewriteHeaders((oldHeaders) => {
      const newHeaders = [...oldHeaders];
      newHeaders.push({
        header: newHeader,
        matcher: { prefix: newPrefix },
        value: newValue,
      });
      delayedOnSave(newHeaders);
      return newHeaders;
    });
    onClose();
  };

  const delayedOnSave = useCallback(
    debounce((rows) => {
      dispatch(
        setRouteFormProperty({ path: "rewriteResponseHeaders", value: rows }),
      );
    }, 150),
    [],
  );

  const onChange = (index: number, path: string, value: string): void => {
    setRewriteHeaders((prevHeaders) => {
      const newHeaders = cloneDeep(prevHeaders);
      set(newHeaders, index + "." + path, value);
      delayedOnSave(newHeaders);
      return newHeaders;
    });
  };

  const onBlur = (): void => {
    delayedOnSave(rewriteHeaders);
  };

  const handleDelete = (index: number) => {
    const newHeaders = [...rewriteHeaders];
    newHeaders.splice(index, 1);
    setRewriteHeaders(newHeaders);
    delayedOnSave(newHeaders);
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        scroll="paper"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          <Grid container spacing={2}>
            <Grid size={10}>
              <Typography variant="h4">Add New Property</Typography>
            </Grid>
            <Grid size={2}>
              <IconButton
                disabled={readonly}
                color="primary"
                onClick={onClose}
                size="large"
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Container disableGutters>
            <Grid container spacing={2} alignItems="stretch">
              <Grid size={12}>
                <Typography variant="body2" gutterBottom>
                  Header Name
                </Typography>
                <TextField
                  disabled={readonly}
                  fullWidth
                  value={newHeader}
                  onChange={(evt): void => {
                    setNewHeader(evt.target.value);
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" gutterBottom>
                  Prefix
                </Typography>
                <TextField
                  disabled={readonly}
                  fullWidth
                  value={newPrefix}
                  onChange={(evt): void => {
                    setNewPrefix(evt.target.value);
                  }}
                />
              </Grid>
              <Grid size={12}>
                <Typography variant="body2" gutterBottom>
                  Value
                </Typography>
                <TextField
                  disabled={readonly}
                  fullWidth
                  value={newValue}
                  onChange={(evt): void => {
                    setNewValue(evt.target.value);
                  }}
                />
              </Grid>
            </Grid>
          </Container>
        </DialogContent>
        <DialogActions>
          <Button
            disabled={!newHeader || readonly}
            variant="contained"
            color="primary"
            onClick={addNewRow}
          >
            Add Header
          </Button>
        </DialogActions>
      </Dialog>
      <Grid container rowSpacing={1}>
        <Grid container spacing={2}>
          {!!rewriteHeaders.length && (
            <>
              <Grid size={3}>
                <Typography variant="body2" gutterBottom>
                  HEADER
                </Typography>
                <Divider />
              </Grid>
              <Grid size={3}>
                <Typography variant="body2" gutterBottom>
                  PREFIX
                </Typography>
                <Divider />
              </Grid>

              <Grid size={4}>
                <Typography variant="body2" gutterBottom>
                  VALUE
                </Typography>
                <Divider />
              </Grid>
            </>
          )}
          <Grid size={2}>
            <IconButton
              disabled={readonly}
              size="small"
              onClick={(): void => setOpen(true)}
            >
              <CirclePlus color="primary" />
            </IconButton>
          </Grid>
        </Grid>
        {rewriteHeaders.map((row, index) => (
          <Grid key={"rewriteHeaders" + index} container spacing={2}>
            <Grid size={3}>
              <FormLabel>{get(row, "header", "")}</FormLabel>
            </Grid>
            <Grid size={3}>
              <TextField
                disabled={readonly}
                fullWidth
                value={get(row, "matcher.prefix", "")}
                onChange={(e) =>
                  onChange(index, "matcher.prefix", e.target.value)
                }
                onBlur={onBlur}
              />
            </Grid>
            <Grid size={4}>
              <TextField
                disabled={readonly}
                fullWidth
                value={get(row, "value", "")}
                onChange={(e) => onChange(index, "value", e.target.value)}
                onBlur={onBlur}
              />
            </Grid>
            <Grid size={2}>
              <IconButton
                disabled={readonly}
                size="small"
                onClick={(): void => handleDelete(index)}
                sx={{
                  pt: 1,
                }}
              >
                <CircleMinus color="primary" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

export default RewriteHeaders;
